import React, { useState } from 'react';
import styled from 'styled-components';
import { FaExclamationTriangle, FaTrash, FaEye, FaTimes, FaCreditCard } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { parseNotificationMetadata } from '../../types/notifications';

/**
 * OverLimitBanner Component
 * Displays storage limit notifications with action buttons
 * 
 * @param {Object} props
 * @param {Object} props.notification - Notification payload
 * @param {Function} props.onClose - Callback when notification is dismissed
 * @param {Function} props.onDeleteVideo - Callback when video is deleted
 * @param {boolean} [props.isLoading] - Loading state for delete action
 */
const OverLimitBanner = ({ 
  notification, 
  onClose, 
  onDeleteVideo,
  isLoading = false 
}) => {
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Parse metadata to get suggested video ID
  const metadata = parseNotificationMetadata(notification);
  const suggestedVideoId = metadata.suggestedVideoId;

  /**
   * Handle delete oldest video action
   */
  const handleDeleteOldest = async () => {
    if (!suggestedVideoId) {
      toast.error('No video suggested for deletion');
      return;
    }

    if (!window.confirm(
      'Are you sure you want to delete your oldest video? This action cannot be undone.'
    )) {
      return;
    }

    try {
      setIsDeleting(true);
      const success = await onDeleteVideo(suggestedVideoId);
      
      if (success) {
        // Mark notification as read/dismissed
        onClose(notification.id);
        toast.success('Oldest video deleted successfully');
      }
    } catch (error) {
      toast.error('Failed to delete video. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  /**
   * Handle review library action
   */
  const handleReviewLibrary = () => {
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    } else {
      // Fallback to library page
      window.location.href = '/library';
    }
  };

  /**
   * Handle browse plans action
   */
  const handleBrowsePlans = () => {
    window.location.href = '/pricing';
  };

  /**
   * Handle dismiss action
   */
  const handleDismiss = () => {
    onClose(notification.id);
  };

  return (
    <BannerContainer>
      <BannerContent>
        <IconContainer>
          <FaExclamationTriangle />
        </IconContainer>
        
        <TextContent>
          <Title>{notification.title}</Title>
          <Body>{notification.body}</Body>
          <AdditionalInfo>
            We'll automatically delete your oldest video in 14 days if you don't free up space.
          </AdditionalInfo>
        </TextContent>

        <ActionButtons>
          {suggestedVideoId && (
            <ActionButton
              onClick={handleDeleteOldest}
              disabled={isDeleting || isLoading}
              variant="primary"
            >
              <FaTrash />
              {isDeleting ? 'Deleting...' : (notification.actionLabel || 'Delete oldest')}
            </ActionButton>
          )}
          
          <ActionButton
            onClick={handleReviewLibrary}
            variant="secondary"
          >
            <FaEye />
            Review library
          </ActionButton>
          
          <ActionButton
            onClick={handleBrowsePlans}
            variant="secondary"
          >
            <FaCreditCard />
            Browse Plans
          </ActionButton>
          
          <DismissButton
            onClick={handleDismiss}
            aria-label="Dismiss notification"
          >
            <FaTimes />
          </DismissButton>
        </ActionButtons>
      </BannerContent>
    </BannerContainer>
  );
};

// Styled Components
const BannerContainer = styled.div`
  background: linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%);
  color: white;
  padding: 1rem;
  margin: 0;
  box-shadow: 0 4px 12px rgba(255, 107, 107, 0.3);
  border-radius: 8px;
  position: relative;
  z-index: 1000;
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const IconContainer = styled.div`
  font-size: 1.5rem;
  color: #fff;
  flex-shrink: 0;
`;

const TextContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const Title = styled.h3`
  margin: 0 0 0.5rem 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #fff;
`;

const Body = styled.p`
  margin: 0 0 0.5rem 0;
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.4;
`;

const AdditionalInfo = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.8);
  font-style: italic;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
  flex-shrink: 0;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  ${props => props.variant === 'primary' && `
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.3);
    
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.3);
      border-color: rgba(255, 255, 255, 0.5);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: transparent;
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.5);
    
    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
      border-color: rgba(255, 255, 255, 0.7);
    }
  `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const DismissButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
`;

export default OverLimitBanner;
